import React, { useMemo } from 'react';
import torchEmpty from './assets/torch-empty.png';
import torchFull from './assets/torch-full.png';
import { motion, AnimatePresence } from 'framer-motion';
import uiTorchGlitch from './assets/UI_TORCH_glitch.png';

// Import fire animation frames
import fire1 from './assets/fire-1.png';
import fire2 from './assets/fire-2.png';
import fire3 from './assets/fire-3.png';
import fire4 from './assets/fire-4.png';
import fire5 from './assets/fire-5.png';
import fire6 from './assets/fire-6.png';
import fire7 from './assets/fire-7.png';
import fire8 from './assets/fire-8.png';
import fire9 from './assets/fire-9.png';

const fireFrames = [fire1, fire2, fire3, fire4, fire5, fire6, fire7, fire8, fire9];
const FIRE_FRAME_DELAY_ACTIVE_MS = 100;
const FIRE_FRAME_DELAY_INACTIVE_MS = 180;

const FireAnimation = ({ x = 0, y = 0, scale = 1, level = 0, className }) => {
  const [currentFrame, setCurrentFrame] = React.useState(0);
  const [isDocumentVisible, setIsDocumentVisible] = React.useState(() => (
    typeof document === 'undefined' ? true : document.visibilityState !== 'hidden'
  ));
  const [isWindowFocused, setIsWindowFocused] = React.useState(() => (
    typeof document === 'undefined' || typeof document.hasFocus !== 'function'
      ? true
      : document.hasFocus()
  ));

  React.useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return undefined;

    const handleVisibilityChange = () => {
      setIsDocumentVisible(document.visibilityState !== 'hidden');
      if (document.visibilityState === 'hidden') {
        setIsWindowFocused(false);
      } else if (typeof document.hasFocus === 'function') {
        setIsWindowFocused(document.hasFocus());
      }
    };

    const handleFocus = () => setIsWindowFocused(true);
    const handleBlur = () => setIsWindowFocused(false);

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  React.useEffect(() => {
    if (level <= 0 || !isDocumentVisible) return undefined;

    let timeoutId = null;
    let cancelled = false;

    const scheduleNextFrame = () => {
      const delay = isWindowFocused
        ? FIRE_FRAME_DELAY_ACTIVE_MS
        : FIRE_FRAME_DELAY_INACTIVE_MS;

      timeoutId = window.setTimeout(() => {
        if (cancelled) return;
        setCurrentFrame((prev) => (prev + 1) % fireFrames.length);
        scheduleNextFrame();
      }, delay);
    };

    scheduleNextFrame();

    return () => {
      cancelled = true;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [level, isDocumentVisible, isWindowFocused]);

  React.useEffect(() => {
    if (level <= 0) {
      setCurrentFrame(0);
    }
  }, [level]);

  // Only render if torch is lit (level > 0)
  if (level <= 0) return null;

  // Calculate scale based on torch level with a minimum size
  const minScale = 0.3; // Minimum scale (30% of full size)
  const levelScale = minScale + ((level / 100) * (1 - minScale));
  const finalScale = scale * levelScale;

  return (
    <motion.img
      src={fireFrames[currentFrame]}
      alt="Fire animation"
      className={`absolute pointer-events-none z-49 ${className}`}
      style={{
        left: `${x}px`,
        top: `${y}px`,
        transform: `scale(${finalScale})`,
        transformOrigin: 'bottom center',
        width: '60px',
        height: '80px',
        transition: 'transform 0.5s ease-out',
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.1 }}
    />
  );
};

const TorchMeter = ({ level, rotation = 130, torches, maxTorches = 3, glitchBurst = null }) => {

  const fillPercent = Math.max(0, Math.min(level, 100)); // Ensure level is between 0 and 100
  const angle = (fillPercent / 100) * 180; // Angle should range from 0 to 180 degrees

  const radius = 70; // Radius of the arc (half the height of the container)
  const center = 70; // Center of the arc

  // Optional: Shift the center of the arc to move it around
  const arcOffsetX = 0; // You can adjust this to move the arc left or right
  const arcOffsetY = -10; // You can adjust this to move the arc up or down

  const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  };

  const describeArc = (x, y, radius, endAngle) => {
    // Apply rotation by adjusting the start angle of the arc
    const startAngle = rotation; // Rotation angle (in degrees)
    const start = polarToCartesian(x + arcOffsetX, y + arcOffsetY, radius, startAngle);
    const end = polarToCartesian(x + arcOffsetX, y + arcOffsetY, radius, startAngle + endAngle);
    const largeArcFlag = endAngle <= 180 ? 0 : 1;

    return [
      'M', start.x, start.y,  // Move to start point
      'A', radius, radius, 0, largeArcFlag, 1, end.x, end.y,  // Arc command
      'L', x + arcOffsetX, y + arcOffsetY,  // Draw line back to center with offset
      'Z'  // Close the path
    ].join(' ');
  };

  // Memoize the arc path to avoid recalculating on each render unless the angle changes
  const arcPath = useMemo(() => describeArc(center, center, radius, angle), [angle, rotation]);

  return (
    <div className="relative w-[140px] h-[125px]">
      {/* Base (empty image) */}
      <img
        src={torchEmpty}
        alt="Torch Empty"
        className="absolute inset-0 w-full h-full z-20 pointer-events-none"
        style={{ width: '100%', height: '100%' }}
      />

      {/* SVG mask over the full image */}
      <svg
        className="absolute inset-0 w-full h-full z-30"
        viewBox="0 0 140 125"
        preserveAspectRatio="none"
        style={{ width: '100%', height: '100%' }}
      >
        <defs>
          <mask id="torch-mask">
            <rect width="100%" height="100%" fill="black" />
            <motion.path 
              d={arcPath} 
              fill="white"
              initial={{ scale: 0 }}
              animate={{ 
                scale: 1,
                transition: { 
                  duration: 0.5,
                  ease: "easeOut"
                }
              }}
            />
          </mask>
        </defs>

        <image
          href={torchFull}
          width="140"
          height="125"
          mask="url(#torch-mask)"
          preserveAspectRatio="none"
        />
      </svg>

      {/* Fire animation overlay - only shown when torch is lit */}
      <FireAnimation 
        x={45}
        y={-52}
        scale={1.5}
        level={level}
        className="z-10"
      />

      <AnimatePresence initial={false}>
        {glitchBurst && (
          <motion.img
            key={glitchBurst.id}
            src={uiTorchGlitch}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full pointer-events-none select-none"
            style={{ mixBlendMode: 'screen', zIndex: 35 }}
            initial={{ opacity: 0.0, x: 0, y: 0 }}
            animate={{
              opacity: [0.0, 1.0, 0.82, 0.0],
              x: [0, glitchBurst.jitterX || 0, (glitchBurst.jitterX || 0) * -0.45, 0],
              y: [0, glitchBurst.jitterY || 0, (glitchBurst.jitterY || 0) * -0.45, 0],
            }}
            exit={{ opacity: 0 }}
            transition={{
              duration: Math.max(0.14, (Number(glitchBurst.durationMs) || 180) / 1000),
              ease: 'easeOut',
            }}
          />
        )}
      </AnimatePresence>

      {/* Torch Count */}
      <div 
        className="absolute text-orange-400 font-bold text-xl font-karma z-40"
        style={{ 
          top: '63.5%', 
          left: '80%', 
          transform: 'translate(-50%, -50%)',
          textShadow: '0 0 8px rgba(255, 100, 0, 0.7)'
        }}
      >
        {torches}/{maxTorches}
      </div>
    </div>
  );
};

export default TorchMeter;
